import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Login from './components/Login';
import NavBar from './components/NavBar';
import LoadingSpinner from './components/LoadingSpinner';
import Landing from './pages/Landing';
import RecentlyAdded from './pages/RecentlyAdded';
import ComingSoon from './pages/ComingSoon';
import Catalogue from './pages/Catalogue';
import Requests from './pages/Requests';
import ReportIssues from './pages/ReportIssues';
import Admin from './pages/Admin';
import MediaDetail from './pages/MediaDetail';

const appBackgroundSx = {
  minHeight: '100vh',
  backgroundImage: 'linear-gradient(155deg, #030202 0%, #070304 52%, #0e0404 100%)',
  backgroundAttachment: 'fixed',
};

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        setCheckingAccess(true);
        try {
          const email = (firebaseUser.email || '').toLowerCase();
          const whitelistRef = doc(db, 'whitelist', email);
          const adminRef = doc(db, 'config', 'admin');
          const [whitelistSnap, adminSnap] = await Promise.all([
            getDoc(whitelistRef),
            getDoc(adminRef),
          ]);

          const adminEmailsRaw = adminSnap.exists() && Array.isArray(adminSnap.data()?.emails)
            ? adminSnap.data().emails
            : [];
          const adminEmails = adminEmailsRaw
            .map(e => String(e).trim().toLowerCase())
            .filter(Boolean);

          setIsWhitelisted(whitelistSnap.exists());
          setIsAdmin(adminEmails.includes(email));
        } catch (err) {
          console.error('Access check failed:', err);
          setIsWhitelisted(false);
          setIsAdmin(false);
        } finally {
          setCheckingAccess(false);
        }
      } else {
        setIsWhitelisted(false);
        setIsAdmin(false);
      }
      setAuthLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleSignOut = () => signOut(auth);

  const handleTabChange = (newTab) => {
    setSelectedItem(null);
    setActiveTab(newTab);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  if (authLoading || checkingAccess) {
    return (
      <Box sx={{ ...appBackgroundSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner message={authLoading ? 'Loading…' : 'Checking access…'} />
      </Box>
    );
  }

  if (!user) return <Login />;

  if (!isWhitelisted) {
    return (
      <Box sx={{ ...appBackgroundSx, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <Paper elevation={8} sx={{ p: 6, maxWidth: 400, width: '100%', textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h2" sx={{ mb: 2 }}>🚫</Typography>
          <Typography variant="h5" color="error" sx={{ mb: 1.5 }}>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Your account ({user.email}) is not on the access list.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3.5 }}>
            Please contact an admin to be granted access.
          </Typography>
          <Button variant="outlined" color="inherit" onClick={handleSignOut}>
            Sign Out
          </Button>
        </Paper>
      </Box>
    );
  }

  const renderPage = () => {
    if (selectedItem) {
      return <MediaDetail item={selectedItem} onBack={() => setSelectedItem(null)} isAdmin={isAdmin} />;
    }
    switch (activeTab) {
      case 'home': return <Landing onItemClick={handleItemClick} onNavigate={handleTabChange} />;
      case 'recentlyAdded': return <RecentlyAdded onItemClick={handleItemClick} />;
      case 'comingSoon': return <ComingSoon onItemClick={handleItemClick} />;
      case 'catalogue': return <Catalogue onItemClick={handleItemClick} />;
      case 'requests': return <Requests user={user} />;
      case 'reportIssues': return <ReportIssues user={user} />;
      case 'admin': return isAdmin ? <Admin /> : null;
      default: return <Landing onItemClick={handleItemClick} onNavigate={handleTabChange} />;
    }
  };

  return (
    <Box sx={appBackgroundSx}>
      <NavBar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isAdmin={isAdmin}
        user={user}
        onSignOut={handleSignOut}
      />
      {renderPage()}
    </Box>
  );
}

export default App;
